import React, { useState } from "react";
import { Icon } from "semantic-ui-react";
import { isMobile } from "react-device-detect";
import "../css/home.css";

const TA_SCORE_MAX = 100;

// ── Design tokens (match CompanyProfile / FA2Consolidated) ───────────────────────
const C = {
  textPrimary: "#0f1f35",
  textSecondary: "#3d5166",
  textMuted: "#7a92a8",
  bg: "#ffffff",
  card: "#ffffff",
  cardHeader: "#f5f2ed",
  cardHeaderText: "#1a2744",
  border: "#d0dcea",
  borderLight: "#e8eef6",
  navyDark: "#0f2d52",
  navy: "#1a4075",
  navyMid: "#2c5282",
  navyLight: "#dce8f5",
  success: "#0a8a7e",
  successBg: "#e6f9f7",
  warning: "#b86b00",
  warningBg: "#fdf3e3",
  danger: "#b91c1c",
  dangerBg: "#fde8e8",
};

const MONO = "'DM Mono', 'Fira Mono', 'Courier New', monospace";
const SANS = "'Lato', 'Helvetica Neue', Arial, sans-serif";

function getTAMeterColor(score) {
  if (score == null || score <= 0) return C.border;
  const s = Number(score);
  if (s >= 80) return C.success;
  if (s >= 60) return "#0a8a7e";
  if (s >= 40) return C.warning;
  if (s >= 20) return "#b86b00";
  return C.danger;
}

function getRecColor(rec) {
  if (!rec) return C.textMuted;
  if (rec.startsWith("Strong")) return C.success;
  if (rec.startsWith("Good")) return "#0a8a7e";
  if (rec.startsWith("Neutral")) return C.warning;
  if (rec.startsWith("Weak")) return C.warning;
  if (rec.startsWith("Avoid")) return C.danger;
  return C.danger;
}

// ── Score display (match FA2 Beta: meter bar + pill) ────────────────────────────
function TAScoreMeter({ score }) {
  const s = score != null ? Math.min(TA_SCORE_MAX, Math.max(0, Number(score))) : 0;
  const pct = (s / TA_SCORE_MAX) * 100;
  const col = getTAMeterColor(s);
  return (
    <div style={{ display: "flex", alignItems: "center", gap: "0.6rem", minWidth: 140 }}>
      <div style={{
        flex: 1, height: 8,
        backgroundColor: C.borderLight,
        borderRadius: 4, overflow: "hidden",
      }}>
        <div style={{
          width: `${pct}%`, height: "100%",
          backgroundColor: col, borderRadius: 4,
          transition: "width 0.4s ease",
        }} />
      </div>
      <span style={{
        fontSize: "0.96rem", fontWeight: 800,
        color: col, minWidth: 52,
        fontFamily: MONO, fontVariantNumeric: "tabular-nums",
      }}>
        {score != null ? `${s}/${TA_SCORE_MAX}` : "—"}
      </span>
    </div>
  );
}

function TAScorePill({ recommendation }) {
  if (!recommendation) return null;
  const col = getRecColor(recommendation);
  return (
    <span style={{
      fontSize: "0.85rem", fontWeight: 700,
      color: col, fontFamily: SANS,
      padding: "0.2rem 0.65rem",
      borderRadius: 20,
      backgroundColor: `${col}18`,
      border: `1.5px solid ${col}40`,
    }}>
      {recommendation}
    </span>
  );
}

// TA category config: metrics = input values shown in expanded section (why this score)
const TA_CATEGORY_ORDER = [
  { id: "trend", label: "Trend", max: 25, icon: "line graph", color: "#4f46e5", signalKeywords: ["sma", "alignment"],
    metrics: [{ label: "SMA 20", key: "sma20", sub: "%" }, { label: "SMA 50", key: "sma50", sub: "%" }, { label: "SMA 200", key: "sma200", sub: "%" }, { label: "52W High", key: "high52W", sub: "%" }, { label: "52W Low", key: "low52W", sub: "%" }] },
  { id: "support", label: "Support", max: 15, icon: "anchor", color: "#0a8a7e", signalKeywords: ["52w", "short-term perf", "chase"],
    metrics: [{ label: "52W Low", key: "low52W", sub: "%" }, { label: "52W High", key: "high52W", sub: "%" }, { label: "Perf Month", key: "perfMonth", sub: "%" }] },
  { id: "rsi", label: "RSI", max: 20, icon: "chart line", color: "#b86b00", signalKeywords: ["rsi", "overbought", "oversold"],
    metrics: [{ label: "RSI (14)", key: "rsi" }] },
  { id: "volume", label: "Volume", max: 15, icon: "sound", color: "#1a4075", signalKeywords: ["volume"],
    metrics: [{ label: "Relative volume", key: "relVolume" }] },
  { id: "perf", label: "Short-term perf", max: 10, icon: "calendar alternate", color: "#2c5282", signalKeywords: ["short-term perf stable"],
    metrics: [{ label: "Perf Week", key: "perfWeek", sub: "%" }, { label: "Perf Month", key: "perfMonth", sub: "%" }, { label: "Perf Quarter", key: "perfQuart", sub: "%" }, { label: "Perf YTD", key: "perfYTD", sub: "%" }] },
  { id: "breakout", label: "Breakout", max: 10, icon: "arrow up", color: "#0a8a7e", signalKeywords: ["breakout", "near 52w high"],
    metrics: [{ label: "SMA 20", key: "sma20", sub: "%" }, { label: "SMA 50", key: "sma50", sub: "%" }, { label: "52W High", key: "high52W", sub: "%" }] },
  { id: "penalty", label: "Penalty", max: 20, icon: "warning sign", color: "#b91c1c", signalKeywords: ["downtrend"], isPenalty: true,
    metrics: [{ label: "RSI", key: "rsi" }, { label: "SMA 20", key: "sma20", sub: "%" }, { label: "SMA 50", key: "sma50", sub: "%" }] },
];

function getTACategoryScore(breakdown, id) {
  if (id === "trend") return breakdown.trend;
  if (id === "support") return breakdown.support;
  if (id === "rsi") return breakdown.rsi;
  if (id === "volume") return breakdown.volume;
  if (id === "perf") return breakdown.perf;
  if (id === "breakout") return breakdown.breakout;
  if (id === "penalty") return breakdown.penalty;
  return null;
}

function getTACategoryReasoning(reasoning, id) {
  if (id === "trend") return reasoning.trend;
  if (id === "support") return reasoning.support;
  if (id === "rsi") return reasoning.rsi;
  if (id === "volume") return reasoning.volume;
  if (id === "perf") return reasoning.perf;
  if (id === "breakout") return reasoning.breakout;
  if (id === "penalty") return reasoning.penalty;
  return "";
}

// Expandable category section: input values + "How we got this score" (reasoning); no redundant signals list
function TACategorySection({ category, score, reasoningText, profile, defaultOpen }) {
  const [open, setOpen] = useState(defaultOpen);
  const max = category.max;
  const isPenalty = category.isPenalty;
  const raw = score != null ? Number(score) : null;
  const effectiveMax = isPenalty ? 20 : max;
  const pct = raw != null && effectiveMax > 0 ? Math.min(100, Math.max(0, (raw / effectiveMax) * 100)) : 0;
  const barPct = isPenalty ? (raw || 0) * 5 : pct;
  const col = raw == null ? C.border : isPenalty ? (raw > 0 ? C.danger : C.textMuted) : getTAMeterColor(pct);
  const display = raw != null ? (isPenalty ? `${raw > 0 ? "-" : ""}${raw}` : `${raw}/${max}`) : "—";
  const metrics = category.metrics || [];
  const hasReasoning = reasoningText && String(reasoningText).trim();

  return (
    <div style={{ borderBottom: `1px solid ${C.border}` }}>
      <div
        onClick={() => setOpen(!open)}
        style={{
          display: "flex", alignItems: "center", justifyContent: "space-between",
          flexWrap: isMobile ? "wrap" : "nowrap", gap: "0.5rem",
          padding: isMobile ? "0.9rem 1.1rem" : "1rem 1.5rem",
          cursor: "pointer", userSelect: "none",
          backgroundColor: C.cardHeader,
          borderBottom: `1px solid ${C.borderLight}`,
        }}
      >
        <div style={{ display: "flex", alignItems: "center", gap: "0.8rem" }}>
          <div style={{
            width: 32, height: 32, borderRadius: 8,
            backgroundColor: C.bg, display: "flex", alignItems: "center", justifyContent: "center",
            flexShrink: 0, border: `1px solid ${C.borderLight}`,
          }}>
            <Icon name={category.icon} style={{ color: C.navy, margin: 0, fontSize: "0.95rem" }} />
          </div>
          <span style={{ fontWeight: 700, fontSize: "1rem", color: C.cardHeaderText, fontFamily: SANS }}>
            {category.label}
          </span>
        </div>
        <div style={{ display: "flex", alignItems: "center", gap: "0.8rem" }}>
          <span style={{
            fontSize: "0.85rem", fontWeight: 700, color: col, fontFamily: MONO,
            padding: "0.15rem 0.55rem", borderRadius: 20,
            backgroundColor: `${col}15`, border: `1.5px solid ${col}35`,
          }}>
            {display}
          </span>
          <div style={{ width: 80, height: 6, backgroundColor: C.borderLight, borderRadius: 3, overflow: "hidden" }}>
            <div style={{
              width: `${barPct}%`, height: "100%", backgroundColor: col, borderRadius: 3,
            }} />
          </div>
          <Icon name={open ? "chevron down" : "chevron right"} size="small" style={{ color: C.navy, flexShrink: 0 }} />
        </div>
      </div>
      {open && (
        <div style={{ backgroundColor: C.bg, borderTop: `1px solid ${C.borderLight}`, padding: "0.9rem 1.5rem 0.9rem 2.6rem" }}>
          {metrics.length > 0 && (
            <div style={{ marginBottom: "0.75rem" }}>
              <div style={{ fontSize: "0.8rem", fontWeight: 600, color: C.textPrimary, fontFamily: SANS, marginBottom: "0.35rem" }}>
                Input values
              </div>
              <div style={{ display: "flex", flexDirection: "column", gap: 0 }}>
                {metrics.map((m, i) => (
                  <MetricRow key={i} label={m.label} value={profile && profile[m.key]} sub={m.sub} />
                ))}
              </div>
            </div>
          )}
          {hasReasoning ? (
            <div>
              <div style={{ fontSize: "0.8rem", fontWeight: 600, color: C.textPrimary, fontFamily: SANS, marginBottom: "0.35rem" }}>
                How we got this score
              </div>
              <p style={{ margin: 0, fontSize: "0.88rem", color: C.textSecondary, lineHeight: 1.6, fontFamily: SANS }}>
                {reasoningText}
              </p>
            </div>
          ) : (
            <div style={{ fontSize: "0.84rem", color: C.textMuted, fontFamily: SANS }}>
              No reasoning available. Re-run the daily calc job to populate.
            </div>
          )}
        </div>
      )}
    </div>
  );
}

// ── Page header (match FA2) ─────────────────────────────────────────────────────
function PageHeader({ title, subtitle, right }) {
  return (
    <div
      style={{
        display: "flex",
        alignItems: "flex-start",
        justifyContent: "space-between",
        flexWrap: "wrap",
        gap: "1rem",
        paddingBottom: "1.25rem",
        marginBottom: "2rem",
        borderBottom: `1px solid ${C.borderLight}`,
      }}
    >
      <div style={{ borderLeft: `4px solid ${C.navy}`, paddingLeft: "1.25rem" }}>
        <h2
          style={{
            margin: 0,
            fontSize: isMobile ? "1.5rem" : "1.75rem",
            fontWeight: 700,
            color: C.cardHeaderText,
            letterSpacing: "-0.01em",
            fontFamily: SANS,
            lineHeight: 1.2,
          }}
        >
          {title}
        </h2>
        {subtitle && (
          <p
            style={{
              margin: "0.35rem 0 0",
              fontSize: "0.9rem",
              color: C.textSecondary,
              fontFamily: SANS,
            }}
          >
            {subtitle}
          </p>
        )}
      </div>
      {right && <div style={{ flexShrink: 0 }}>{right}</div>}
    </div>
  );
}

// ── Row for a single TA metric ──────────────────────────────────────────────
function MetricRow({ label, value, sub }) {
  const hasVal = value != null && value !== "" && !Number.isNaN(Number(value));
  const num = hasVal ? Number(value) : null;
  const isPct = sub === "%";
  let display = "—";
  if (hasVal && num !== null) {
    if (isPct) {
      display = (num >= 0 ? "+" : "") + num.toFixed(2) + "%";
    } else {
      display = num % 1 === 0 ? String(num) : num.toFixed(2);
    }
  }
  return (
    <div
      style={{
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
        padding: "0.75rem 1.25rem",
        borderBottom: `1px solid ${C.borderLight}`,
        gap: "1rem",
        flexWrap: "wrap",
      }}
    >
      <span
        style={{
          fontWeight: 600,
          fontSize: "0.95rem",
          color: C.textPrimary,
          fontFamily: SANS,
        }}
      >
        {label}
      </span>
      <span
        style={{
          fontSize: "0.95rem",
          fontFamily: MONO,
          fontVariantNumeric: "tabular-nums",
          color: hasVal ? C.textPrimary : C.textMuted,
        }}
      >
        {hasVal ? display : "—"}
      </span>
    </div>
  );
}

// ── Collapsible section (match FA2 CategorySection style) ──────────────────────
function TASection({ title, icon, children, defaultOpen }) {
  const [open, setOpen] = useState(defaultOpen !== false);
  return (
    <div style={{ borderBottom: `1px solid ${C.border}` }}>
      <div
        onClick={() => setOpen(!open)}
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          padding: isMobile ? "0.9rem 1.1rem" : "1rem 1.5rem",
          cursor: "pointer",
          userSelect: "none",
          backgroundColor: C.cardHeader,
          borderBottom: `1px solid ${C.borderLight}`,
        }}
      >
        <div style={{ display: "flex", alignItems: "center", gap: "0.8rem" }}>
          <div
            style={{
              width: 32,
              height: 32,
              borderRadius: 8,
              backgroundColor: C.bg,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              border: `1px solid ${C.borderLight}`,
            }}
          >
            <Icon name={icon} style={{ color: C.navy, margin: 0, fontSize: "0.95rem" }} />
          </div>
          <span
            style={{
              fontWeight: 700,
              fontSize: "1rem",
              color: C.cardHeaderText,
              fontFamily: SANS,
            }}
          >
            {title}
          </span>
        </div>
        <Icon
          name={open ? "chevron down" : "chevron right"}
          size="small"
          style={{ color: C.navy, flexShrink: 0 }}
        />
      </div>
      {open && (
        <div style={{ backgroundColor: C.bg, borderTop: `1px solid ${C.borderLight}` }}>
          {children}
        </div>
      )}
    </div>
  );
}

export default function TA2Consolidated({ stockValues }) {
  if (!stockValues) return null;

  const sr = stockValues.stockReport || {};
  const cp = stockValues.compProfile || {};
  const techScore = sr.technicalScore != null ? Number(sr.technicalScore) : null;
  const techRec = sr.technicalRecommendation || "";

  const breakdown = {
    trend: sr.technicalTrendScore != null ? Number(sr.technicalTrendScore) : null,
    support: sr.technicalSupportScore != null ? Number(sr.technicalSupportScore) : null,
    rsi: sr.technicalRsiScore != null ? Number(sr.technicalRsiScore) : null,
    volume: sr.technicalVolumeScore != null ? Number(sr.technicalVolumeScore) : null,
    perf: sr.technicalPerfScore != null ? Number(sr.technicalPerfScore) : null,
    breakout: sr.technicalBreakoutScore != null ? Number(sr.technicalBreakoutScore) : null,
    penalty: sr.technicalPenaltyScore != null ? Number(sr.technicalPenaltyScore) : null,
  };
  const reasoning = {
    trend: sr.technicalTrendReasoning || "",
    support: sr.technicalSupportReasoning || "",
    rsi: sr.technicalRsiReasoning || "",
    volume: sr.technicalVolumeReasoning || "",
    perf: sr.technicalPerfReasoning || "",
    breakout: sr.technicalBreakoutReasoning || "",
    penalty: sr.technicalPenaltyReasoning || "",
  };
  const hasBreakdown = Object.values(breakdown).some((v) => v != null);

  const signals = Array.isArray(sr.technicalSignals) ? sr.technicalSignals : [];
  const hasAnyTa =
    techScore != null ||
    techRec ||
    signals.length > 0 ||
    cp.sma20 != null ||
    cp.sma50 != null ||
    cp.sma200 != null ||
    cp.rsi != null ||
    cp.relVolume != null;

  const right = (
    <div style={{ display: "flex", alignItems: "flex-end", gap: "1.5rem", flexWrap: "wrap" }}>
      {techScore != null && (
        <div style={{ textAlign: "right" }}>
          <span
            style={{
              display: "block",
              fontSize: "0.78rem",
              fontWeight: 700,
              letterSpacing: "0.04em",
              color: C.textSecondary,
              fontFamily: SANS,
              marginBottom: "0.2rem",
            }}
          >
            Technical Score
          </span>
          <span
            style={{
              fontSize: "2rem",
              fontWeight: 700,
              color: getTAMeterColor(techScore),
              fontFamily: MONO,
              letterSpacing: "-0.02em",
              lineHeight: 1,
            }}
          >
            {techScore}
            <span style={{ fontSize: "0.5em", color: C.textMuted, fontWeight: 600 }}> /{TA_SCORE_MAX}</span>
          </span>
        </div>
      )}
      {techRec && (
        <span
          style={{
            fontSize: "0.9rem",
            fontWeight: 700,
            color: getRecColor(techRec),
            fontFamily: SANS,
            padding: "0.35rem 0.75rem",
            borderRadius: 20,
            backgroundColor: `${getRecColor(techRec)}18`,
            border: `1.5px solid ${getRecColor(techRec)}40`,
          }}
        >
          {techRec}
        </span>
      )}
    </div>
  );

  return (
    <div
      className="fundamentals-page"
      style={{
        padding: isMobile ? "2rem 0.5rem" : "2rem 1rem",
        width: "100%",
        maxWidth: 1280,
        margin: "0 auto",
        boxSizing: "border-box",
        fontFamily: SANS,
        backgroundColor: C.bg,
      }}
    >
      <PageHeader
        title="Technical Analysis (Beta)"
        subtitle="Price vs moving averages · RSI · Volume · Short-term performance."
        right={techScore != null || techRec ? right : null}
      />

      {hasAnyTa ? (
        <div
          style={{
            backgroundColor: C.bg,
            border: `1px solid ${C.border}`,
            borderRadius: 10,
            overflow: "hidden",
            boxShadow: "0 1px 3px rgba(15,45,82,0.06)",
          }}
        >
          {hasBreakdown && (
            <div style={{ borderBottom: `1px solid ${C.borderLight}` }}>
              <div
                style={{
                  padding: "1rem 1.5rem",
                  borderBottom: `1px solid ${C.borderLight}`,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                  flexWrap: "wrap",
                  gap: "0.75rem",
                  backgroundColor: C.cardHeader,
                }}
              >
                <div>
                  <h3 style={{ margin: 0, fontSize: "1rem", fontWeight: 700, color: C.cardHeaderText, fontFamily: SANS }}>
                    Score breakdown
                  </h3>
                  <p style={{ margin: "0.15rem 0 0", fontSize: "0.84rem", color: C.textSecondary, fontFamily: SANS }}>
                    Points per category (0–100 total). Expand a row to see input values and how the score was calculated.
                  </p>
                </div>
                <div style={{ display: "flex", gap: "0.4rem", flexWrap: "wrap" }}>
                  {TA_CATEGORY_ORDER.map((cat) => {
                    const col = cat.color || C.navy;
                    return (
                      <span
                        key={cat.id}
                        style={{
                          fontSize: "0.76rem", fontWeight: 600,
                          padding: "0.22rem 0.65rem", borderRadius: 20,
                          backgroundColor: `${col}12`, color: col,
                          border: `1.5px solid ${col}28`, fontFamily: SANS,
                        }}
                      >
                        {cat.label}
                      </span>
                    );
                  })}
                </div>
              </div>
              {TA_CATEGORY_ORDER.map((cat, idx) => (
                <TACategorySection
                  key={cat.id}
                  category={cat}
                  score={getTACategoryScore(breakdown, cat.id)}
                  reasoningText={getTACategoryReasoning(reasoning, cat.id)}
                  profile={cp}
                  defaultOpen={idx === 0}
                />
              ))}
            </div>
          )}

          <TASection title="Volatility & risk (reference; not in score)" icon="shield" defaultOpen={false}>
            <MetricRow label="Beta" value={cp.beta} />
            <MetricRow label="ATR" value={cp.atr} />
            <MetricRow label="Volatility (week)" value={cp.volatilityW} sub="%" />
            <MetricRow label="Volatility (month)" value={cp.volatilityM} sub="%" />
          </TASection>

          <div
            style={{
              padding: "0.85rem 1.5rem",
              fontSize: "0.82rem",
              color: C.textSecondary,
              backgroundColor: C.cardHeader,
              borderTop: `1px solid ${C.borderLight}`,
              fontFamily: SANS,
            }}
          >
            <Icon name="info circle" style={{ color: C.textMuted, margin: "0 0.35rem 0 0" }} />
            <strong style={{ color: C.textPrimary }}>Score: </strong>
            trend (25) + support (15) + RSI (20) + volume (15) + short-term perf (10) + breakout (10) − penalty (20). Bands: Strong-TA 80+, Good-TA 60+, Neutral-TA 40+, Weak-TA 20+, Avoid-TA {"<20"}.
          </div>
        </div>
      ) : (
        <div
          style={{
            padding: "3rem 1.5rem",
            textAlign: "center",
            backgroundColor: C.bg,
            borderRadius: 10,
            border: `1px solid ${C.border}`,
          }}
        >
          <Icon name="chart line" size="large" style={{ color: C.textMuted, marginBottom: "0.75rem" }} />
          <p
            style={{
              margin: 0,
              fontSize: "0.95rem",
              color: C.textSecondary,
              lineHeight: 1.6,
              fontFamily: SANS,
            }}
          >
            Technical data is not available yet. 
          </p>
        </div>
      )}
    </div>
  );
}
